/**
 * TRAC-1108: when a session counts as exposed to its starter-prompt arm, and which
 * starter prompt a chip click was. Kept apart from ChatInterface so both rules are
 * tested without rendering the chat.
 */
import type { Message } from '../../types/chat.types';
import type { StarterPrompt } from './WelcomeMessage';
import { effectiveKind } from '../../utils/messageKind';

/**
 * Whether starter prompts are among the messages actually drawn: a welcome with a
 * non-empty follow_up_actions part. The department menu (routing_actions, TRAC-790) is
 * not starter prompts and never counts.
 */
export function starterPromptsOnScreen(messages: readonly Message[]): boolean {
  return messages.some((m) =>
    effectiveKind(m) === 'welcome' &&
    (m.parts || []).some((p) => p.type === 'follow_up_actions' && (p.actions?.length ?? 0) > 0));
}

/**
 * Exposure is the prompts being seen, so it needs the panel open as well as the prompts
 * drawn: inside the page shell the chat loads collapsed on every page view (TRAC-959),
 * and a welcome rendered in a closed panel has been seen by nobody.
 */
export function exposureDue(panelOpen: boolean, messages: readonly Message[], sessionId: string | null): boolean {
  return panelOpen && !!sessionId && starterPromptsOnScreen(messages);
}

/**
 * The starter prompt a chip click was, or undefined. Chips arrive as (label, value) from
 * the welcome and the other way round from other callers, so the text can be in either.
 * With two prompts of the same text the first wins, the same one the chip was built from.
 */
export function matchStarterPrompt(
  prompts: readonly StarterPrompt[],
  value: string,
  label: string,
): StarterPrompt | undefined {
  return prompts.find((p) => p.text === value || p.text === label);
}
